import React, { useEffect, useRef } from "react";
import { ArrowUpRight } from "lucide-react";

/**
 * EmbedSocial aggregated social feed (IG + Facebook + TikTok + X).
 *
 * Reads three env vars from /app/frontend/.env:
 *   REACT_APP_EMBEDSOCIAL_WIDGET_ID   → the data-ref value from your widget
 *   REACT_APP_EMBEDSOCIAL_WIDGET_CLASS → e.g. "embedsocial-hashtag" or "embedsocial-feed"
 *   REACT_APP_EMBEDSOCIAL_SCRIPT_URL  → e.g. "https://embedsocial.com/cdn/ht.js"
 *
 * If REACT_APP_EMBEDSOCIAL_WIDGET_ID is missing/empty, this component renders nothing
 * so the existing manual <SocialFeed /> remains visible.
 */
const SCRIPT_ID = "embedsocial-cdn-script";

export default function EmbedSocialFeed() {
  const containerRef = useRef(null);

  const widgetId = process.env.REACT_APP_EMBEDSOCIAL_WIDGET_ID;
  const widgetClass = process.env.REACT_APP_EMBEDSOCIAL_WIDGET_CLASS || "embedsocial-hashtag";
  const scriptUrl = process.env.REACT_APP_EMBEDSOCIAL_SCRIPT_URL || "https://embedsocial.com/cdn/ht.js";

  useEffect(() => {
    if (!widgetId) return;
    if (document.getElementById(SCRIPT_ID)) return;
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = scriptUrl;
    s.async = true;
    document.head.appendChild(s);
  }, [widgetId, scriptUrl]);

  if (!widgetId) return null;

  return (
    <section
      className="py-16 lg:py-20 border-t border-[var(--border)]"
      data-testid="embedsocial-feed-section"
    >
      <div className="container-nk">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-9">
          <div>
            <p className="eyebrow text-gold">// News &amp; Media</p>
            <h2 className="font-display text-[44px] md:text-[60px] leading-[0.95] mt-3">
              Latest from Naami
            </h2>
            <p className="text-muted text-[14px] mt-3 max-w-md font-light leading-relaxed">
              Live updates pulled from our Instagram, Facebook, TikTok and X
              accounts — straight from the kitchen.
            </p>
          </div>
          <a
            href="https://www.instagram.com/naamikitchens"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-gold hover:gap-3 transition-all"
            data-testid="embedsocial-follow-link"
          >
            Follow us
            <ArrowUpRight size={14} />
          </a>
        </div>

        <div
          ref={containerRef}
          className={widgetClass}
          data-ref={widgetId}
        />
      </div>
    </section>
  );
}
